import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { adminApi } from "../../services/api"

function normalizeSearchText (s) {
  return String(s ?? '').trim()
}

export default function AdminDocumentations ({ students = [] }) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [documentations, setDocumentations] = useState([])
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 })

  const [search, setSearch] = useState("")
  const [studentId, setStudentId] = useState("")
  const [status, setStatus] = useState("")
  const [generationStatus, setGenerationStatus] = useState("")
  const [generationType, setGenerationType] = useState("")

  const [page, setPage] = useState(1)
  const limit = 20

  const fetchDocumentations = async (nextPage = page, overrides = {}) => {
    setLoading(true)
    setError("")
    try {
      const effectiveSearch = overrides.search ?? search
      const effectiveStudentId = overrides.studentId ?? studentId
      const effectiveStatus = overrides.status ?? status
      const effectiveGenerationStatus = overrides.generationStatus ?? generationStatus
      const effectiveGenerationType = overrides.generationType ?? generationType

      const params = {
        page: nextPage,
        limit
      }

      const searchTerm = normalizeSearchText(effectiveSearch)
      if (searchTerm) params.search = searchTerm
      if (effectiveStudentId) params.studentId = effectiveStudentId
      if (effectiveStatus) params.status = effectiveStatus
      if (effectiveGenerationStatus) params.generationStatus = effectiveGenerationStatus
      if (effectiveGenerationType) params.generationType = effectiveGenerationType

      const res = await adminApi.documentations.list(params)
      const data = res?.data?.data
      setDocumentations(data?.documentations || [])
      if (data?.pagination) setPagination(data.pagination)
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to load documentations")
      setDocumentations([])
      setPagination({ page: nextPage, limit, total: 0, pages: 1 })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDocumentations(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearch = () => {
    setPage(1)
    fetchDocumentations(1)
  }

  const clearFilters = () => {
    const nextFilters = { search: "", studentId: "", status: "", generationStatus: "", generationType: "" }
    setSearch(nextFilters.search)
    setStudentId(nextFilters.studentId)
    setStatus(nextFilters.status)
    setGenerationStatus(nextFilters.generationStatus)
    setGenerationType(nextFilters.generationType)
    setPage(1)
    fetchDocumentations(1, nextFilters)
  }

  const openDoc = (doc) => {
    if (!doc?._id) return
    navigate(`/admin/documentations/${doc._id}`)
  }

  return (
    <div>
      <div className="flex gap-2 justify-end mb-3">
        <button className="btn btn-ghost btn-sm" type="button" onClick={clearFilters} disabled={loading}>
          Clear
        </button>
        <button className="btn btn-primary btn-sm" type="button" onClick={handleSearch} disabled={loading}>
          {loading ? "Loading..." : "Search"}
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
        <div className="flex-1">
          <label className="label">Search</label>
          <input
            type="text"
            className="input input-bordered w-full input-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search student name/email or project title..."
          />
        </div>

        <div className="sm:w-56">
          <label className="label">Student</label>
          <select className="select select-bordered w-full select-sm" value={studentId} onChange={(e) => setStudentId(e.target.value)}>
            <option value="">All students</option>
            {students.map((s) => (
              <option key={s._id} value={s._id}>
                {s.name} ({s.email})
              </option>
            ))}
          </select>
        </div>

        <div className="sm:w-44">
          <label className="label">Doc Status</label>
          <select className="select select-bordered w-full select-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="DRAFT">Draft</option>
            <option value="IN_REVIEW">In Review</option>
            <option value="APPROVED">Approved</option>
          </select>
        </div>

        <div className="sm:w-56">
          <label className="label">Type</label>
          <select
            className="select select-bordered w-full select-sm"
            value={generationType}
            onChange={(e) => setGenerationType(e.target.value)}
          >
            <option value="">All</option>
            <option value="DOCUMENTATION">Documentation</option>
            <option value="PROPOSAL">Proposal</option>
            <option value="SIWES_REPORT">SIWES Report</option>
          </select>
        </div>

        <div className="sm:w-56">
          <label className="label">Generation</label>
          <select
            className="select select-bordered w-full select-sm"
            value={generationStatus}
            onChange={(e) => setGenerationStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="COMPLETE">Complete</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="FAILED">Failed</option>
          </select>
        </div>
      </div>

      {error && <p className="text-error mb-2">{error}</p>}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <span className="loading loading-spinner loading-lg text-primary"></span>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="mb-2 text-sm text-slate-600">
            Showing {documentations.length} result{documentations.length === 1 ? "" : "s"}.
          </div>
          <table className="table table-zebra">
            <thead>
              <tr>
                <th>Student</th>
                <th>Project</th>
                <th>Doc Status</th>
                <th>Type</th>
                <th>Generation</th>
                <th>Chapters</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {documentations.map((d) => (
                <tr key={d._id}>
                  <td>
                    <div className="font-medium">{d.student?.name || "—"}</div>
                    <div className="text-xs text-slate-500">{d.student?.email || "—"}</div>
                  </td>
                  <td>
                    <div className="font-semibold">{d.project?.title || "—"}</div>
                    <div className="text-sm text-slate-600 line-clamp-2">{d.project?.description || ""}</div>
                  </td>
                  <td>
                    <span
                      className={`badge badge-sm ${
                        d.status === "APPROVED" ? "badge-success" : d.status === "IN_REVIEW" ? "badge-warning" : "badge-ghost"
                      }`}
                    >
                      {d.status || "—"}
                    </span>
                  </td>
                  <td>
                    <span className="badge badge-outline badge-sm">
                      {d.generationType === "PROPOSAL"
                        ? "Proposal"
                        : d.generationType === "SIWES_REPORT"
                          ? "SIWES"
                          : "Documentation"}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`badge badge-sm ${
                        d.generationStatus === "COMPLETE"
                          ? "badge-success"
                          : d.generationStatus === "IN_PROGRESS"
                            ? "badge-info"
                            : d.generationStatus === "FAILED"
                              ? "badge-error"
                              : "badge-ghost"
                      }`}
                    >
                      {d.generationStatus || "—"}
                    </span>
                  </td>
                  <td>{Array.isArray(d.chapters) ? d.chapters.length : 0}</td>
                  <td>{d.updatedAt ? new Date(d.updatedAt).toLocaleDateString() : "—"}</td>
                  <td>
                    <button className="btn btn-ghost btn-xs" type="button" onClick={() => openDoc(d)}>
                      View
                    </button>
                  </td>
                </tr>
              ))}
              {documentations.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
                      No documentation found for the current filters.
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>

          {pagination?.pages > 1 ? (
            <div className="flex items-center justify-between mt-3">
              <button
                className="btn btn-sm"
                type="button"
                disabled={page <= 1}
                onClick={() => {
                  const next = Math.max(1, page - 1)
                  setPage(next)
                  fetchDocumentations(next)
                }}
              >
                Prev
              </button>
              <div className="text-sm text-slate-600">
                Page {pagination.page} of {pagination.pages}
              </div>
              <button
                className="btn btn-sm"
                type="button"
                disabled={page >= pagination.pages}
                onClick={() => {
                  const next = page + 1
                  setPage(next)
                  fetchDocumentations(next)
                }}
              >
                Next
              </button>
            </div>
          ) : null}
        </div>
      )}

    </div>
  )
}

